/** @format */

import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { Logger, UtilityService } from '@shared/data-access/services';
import { pageSizeChanged, selectPageSize, selectTotalCount } from '@plp/data-access/state';

import { PAGE_SIZE_OPTIONS } from '@plp/data-access/constants';
import { ProductAnalyticsService } from '@shared/data-access/analytics';
import { Store } from '@ngrx/store';
import { USWholeNumberPipe } from '@shared/utils/pipes';

@Component({
  selector: 'plp-components-top-pagination',
  templateUrl: './top-pagination.component.html',
  styleUrl: './top-pagination.component.scss',
  imports: [USWholeNumberPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopPaginationComponent {
  private readonly analyticsService = inject(ProductAnalyticsService);
  private readonly utilityService = inject(UtilityService);
  private readonly logger = inject(Logger);
  private readonly store = inject(Store);

  readonly isMobile = input(false);
  readonly pageSize$ = this.store.selectSignal(selectPageSize);
  readonly totalCount$ = this.store.selectSignal(selectTotalCount);

  protected readonly pageSizeOptions = computed(() => {
    const totalCount = this.totalCount$();

    if (totalCount <= 48) {
      return [];
    }
    return totalCount <= 96
      ? PAGE_SIZE_OPTIONS.filter((pageSize) => pageSize <= 96)
      : PAGE_SIZE_OPTIONS;
  });

  async pageSizeClickEvent(newPageSize: number, oldPageSize: number): Promise<void> {
    if (newPageSize !== oldPageSize) {
      try {
        await this.utilityService.yieldTask(() => {
          this.store.dispatch(pageSizeChanged({ pageSize: newPageSize }));
          this.setAnalyticsEvent(newPageSize);
        });
      } catch (error) {
        this.logger.error(
          'Error executing yield task or page size change event after page size click:',
          error,
        );
      }
    }
  }

  private setAnalyticsEvent(pageSize: number): void {
    const refinementUpdateObj = { UpdateType: 'ProductPerPage', UpdateValue: pageSize };
    this.analyticsService.triggerAnalyticsEvent('ResultsViewUpdate', refinementUpdateObj);
  }

  trackByFn(index: number, item: number): string {
    return index.toString() + item;
  }
}
